import React, { useEffect, useState } from "react";
import { collection, getDocs } from "firebase/firestore";
import { db } from "../../../firebaseConfig";
import {
  calculateOfficeSchemeFee,
  formatINR,
} from "../../../utils/officeScheme/gstCalculator";

export default function OfficeAccountsRegister() {
  const [apps, setApps] = useState([]);

  const fetchData = async () => {
    const snap = await getDocs(collection(db, "office_scheme_applications"));
    setApps(snap.docs.map((d, i) => ({ id: d.id, seq: i + 1, ...d.data() })));
  };

  useEffect(() => {
    fetchData();
  }, []);

  const totals = apps.reduce(
    (acc, app) => {
      const f = calculateOfficeSchemeFee(app.estimateAmount);
      acc.estimate += f.totalEstimate;
      acc.fee += f.consultancyFee;
      acc.gst += f.totalGst;
      acc.total += f.grandTotal;
      if (app.paymentStatus === "PAID") acc.paid += f.grandTotal;
      else acc.pending += f.grandTotal;
      return acc;
    },
    { estimate: 0, fee: 0, gst: 0, total: 0, paid: 0, pending: 0 }
  );

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-2xl shadow p-6">
        <h2 className="text-2xl font-bold text-slate-800">
          Accounts Register
        </h2>
        <p className="text-sm text-slate-500">
          Invoice register, receipt register, GST summary and outstanding report.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
        <Card title="Estimate" value={formatINR(totals.estimate)} />
        <Card title="Fee 1.5%" value={formatINR(totals.fee)} />
        <Card title="GST" value={formatINR(totals.gst)} />
        <Card title="Invoice Total" value={formatINR(totals.total)} />
        <Card title="Paid" value={formatINR(totals.paid)} />
        <Card title="Outstanding" value={formatINR(totals.pending)} />
      </div>

      <Register title="Invoice Register" apps={apps} type="invoice" />
      <Register title="Receipt Register" apps={apps.filter(a => a.paymentStatus === "PAID")} type="receipt" />
      <Register title="Outstanding Register" apps={apps.filter(a => a.paymentStatus !== "PAID")} type="outstanding" />
    </div>
  );
}

function Register({ title, apps, type }) {
  return (
    <div className="bg-white rounded-2xl shadow p-6 overflow-x-auto">
      <h3 className="text-xl font-bold mb-4">{title}</h3>

      <table className="w-full text-sm border">
        <thead className="bg-slate-100">
          <tr>
            <th className="border p-2">No</th>
            <th className="border p-2">Date</th>
            <th className="border p-2">Society</th>
            <th className="border p-2">District</th>
            <th className="border p-2">Fee</th>
            <th className="border p-2">CGST</th>
            <th className="border p-2">SGST</th>
            <th className="border p-2">Total</th>
            <th className="border p-2">UTR</th>
            <th className="border p-2">Status</th>
          </tr>
        </thead>

        <tbody>
          {apps.map((app, index) => {
            const f = calculateOfficeSchemeFee(app.estimateAmount);

            const no =
              type === "receipt"
                ? app.receiptNo || "-"
                : app.invoiceNo || "-";

            const date =
              type === "receipt"
                ? app.receiptDate || app.paymentDate || "-"
                : app.invoiceDate || "-";

            return (
              <tr key={app.id}>
                <td className="border p-2">{no}</td>
                <td className="border p-2">{date}</td>
                <td className="border p-2 font-semibold">
                  {app.societyName || "-"}
                </td>
                <td className="border p-2">{app.district || "-"}</td>
                <td className="border p-2">{formatINR(f.consultancyFee)}</td>
                <td className="border p-2">{formatINR(f.cgst)}</td>
                <td className="border p-2">{formatINR(f.sgst)}</td>
                <td className="border p-2 font-bold">
                  {formatINR(f.grandTotal)}
                </td>
                <td className="border p-2">{app.utrNo || "-"}</td>
                <td className="border p-2">
                  {app.paymentStatus || "PENDING"}
                </td>
              </tr>
            );
          })}

          {apps.length === 0 && (
            <tr>
              <td colSpan="10" className="border p-4 text-center text-slate-500">
                No records found.
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

function Card({ title, value }) {
  return (
    <div className="bg-white rounded-2xl shadow p-4">
      <p className="text-xs text-slate-500">{title}</p>
      <h3 className="font-bold text-slate-800">{value}</h3>
    </div>
  );
}